// Pure helpers of the batched precedents / dependents tools (PLAN-agent-v2 §13 D6): resolve the
// requested cells (sheet-qualified entries may mix sheets), validate them, and shape the result.
// The Office.js part lives in ExcelToolExecutor (the only Office.js seam).
import { parseCell, splitSheetPrefix, stripSheet } from './a1';

export const MAX_BATCH_CELLS = 50;
export const DEFAULT_MAX_ADDRESSES_PER_CELL = 40;

export type TraceKind = 'precedents' | 'dependents';

export interface BatchCellRef {
  sheet: string;
  cell: string;
  /** Validation error for this entry (never fails the whole call). */
  error?: string;
}

export interface BatchCellResult {
  sheet: string;
  cell: string;
  formula: string | null;
  value: unknown;
  count: number;
  truncated: boolean;
  /** Sheet-qualified addresses ('Sheet'!A1:B2 …). */
  addresses: string[];
  error?: string;
}

export interface BatchOutput {
  kind: TraceKind;
  levels: 1 | 'all';
  results: BatchCellResult[];
  cells_requested: number;
  cells_failed: number;
}

/** Resolve `cells` against the default `sheet`: 'Other!B4' keeps its own sheet, 'B4' uses `sheet`.
 *  Invalid entries carry an `error` instead of throwing; the list is capped at MAX_BATCH_CELLS. */
export function resolveBatchCells(sheet: string, cells: unknown): { refs: BatchCellRef[]; error?: string } {
  if (!Array.isArray(cells) || cells.length === 0) return { refs: [], error: 'cells must be a non-empty array of A1 cells' };
  if (cells.length > MAX_BATCH_CELLS) return { refs: [], error: `at most ${MAX_BATCH_CELLS} cells per call (got ${cells.length})` };
  const refs: BatchCellRef[] = [];
  for (const raw of cells) {
    const text = String(raw ?? '').trim();
    const split = splitSheetPrefix(text);
    const target = split.sheet ?? sheet;
    const cell = stripSheet(split.address);
    if (!target) {
      refs.push({ sheet: '', cell, error: 'no sheet: pass `sheet` or a sheet-qualified cell' });
      continue;
    }
    try {
      parseCell(cell);
      refs.push({ sheet: target, cell });
    } catch (e) {
      refs.push({ sheet: target, cell: text, error: (e as Error).message });
    }
  }
  return { refs };
}

/** Normalise `levels`: 1 (direct) or 'all' (every level). Numbers > 1 mean 'all'. */
export function batchLevels(levels: unknown): 1 | 'all' {
  if (levels === 'all') return 'all';
  const n = Number(levels ?? 1);
  return Number.isFinite(n) && n > 1 ? 'all' : 1;
}

/** Cap the address list of one cell and flag the truncation. */
export function capAddresses(addresses: string[], max: number): { addresses: string[]; truncated: boolean } {
  const cap = Math.max(1, Math.floor(max));
  return { addresses: addresses.slice(0, cap), truncated: addresses.length > cap };
}

export function failedEntry(ref: BatchCellRef, error: string): BatchCellResult {
  return { sheet: ref.sheet, cell: ref.cell, formula: null, value: null, count: 0, truncated: false, addresses: [], error };
}

export function shapeBatch(kind: TraceKind, levels: 1 | 'all', results: BatchCellResult[], requested: number): BatchOutput {
  return { kind, levels, results, cells_requested: requested, cells_failed: results.filter((r) => r.error !== undefined).length };
}
